<template>
  <modal @close="$emit('cancel')">
    <div slot="header"><strong>{{ isEditing ? 'Backdate BOY Status' : 'Add New BOY Status' }}</strong></div>
    <div slot="body">
      <form novalidate @submit.prevent="handleSubmit">
        <div class="f-form">
          <div class="f-form__full">
            <f-group label="BOY Status" id="boy_status_id" required>
              <multiselect
                v-model="form.boyStatus"
                :options="statusOptions"
                label="name"
                track-by="id"
                :searchable="false"
                select-label=""
                deselect-label=""
                selected-label=""
              />
            </f-group>
          </div>
          <div class="f-form__full">
            <f-group label="Status" id="status_id" required>
              <multiselect
                v-model="form.status"
                :options="statusOptions"
                label="name"
                track-by="id"
                :searchable="false"
                select-label=""
                deselect-label=""
                selected-label=""
              />
            </f-group>
          </div>
          <div class="f-form__full">
            <f-text id="effective_date" label="Effective Date" type="date" v-model="form.effectiveDate" required />
          </div>
          <div class="f-form__full" v-if="type === 'rider' && !isEditing">
            <f-checkbox id="use_auto_zone" v-model="form.useAutoZone" label="Automatically determine zone" />
          </div>
          <div class="f-form__full" v-if="type === 'rider' && (isEditing || !form.useAutoZone)">
            <f-group label="Zone" id="zone_id" :required="!isEditing">
              <multiselect
                v-model="form.zone"
                :options="zoneOptions"
                :loading="zoneLoading"
                label="name"
                track-by="id"
                :searchable="false"
                select-label=""
                deselect-label=""
                selected-label=""
              />
            </f-group>
          </div>
          <div class="f-form__full" v-if="type === 'horse' && isEditing">
            <f-search-select
              id="owner_id"
              label="Owner"
              v-model="form.owner"
              :options="ownerOptionsWithSelected"
              :loading="ownerLoading"
              option-label="name"
              :custom-label="ownerLabel"
              placeholder="Search members..."
              required
              @select="onOwnerSelect"
              @search-change="onOwnerSearch"
              @open="onOwnerSearch('')"
            />
          </div>
        </div>
      </form>
      <div class="alert alert-danger mt-3 mb-0" v-if="error || validationError">{{ error || validationError }}</div>
    </div>
    <div slot="footer">
      <app-button variant="outline-primary" :block="false" @click="$emit('cancel')">Cancel</app-button>
      <app-button class="ml-2" :block="false" :loading="saving" @click="handleSubmit">Save</app-button>
    </div>
  </modal>
</template>

<script>
import {
  defineComponent, reactive, ref, computed
} from '@vue/composition-api'
import Modal from '@/components/Modal'
import AppButton from '@/components/AppButton'
import FText from '@/components/FormFields/FText'
import FCheckbox from '@/components/FormFields/FCheckbox'
import FGroup from '@/components/FormFields/Base/FGroup'
import FSearchSelect from '@/components/FormFields/FSearchSelect'
import Multiselect from '@/components/AppMultiselect'
import useZones from '@/composables/useZones'

export default defineComponent({
  name: 'BoyStatusFormModal',
  components: {
    Modal, AppButton, FText, FCheckbox, FGroup, FSearchSelect, Multiselect
  },
  props: {
    type: { type: String, required: true },
    statusOptions: { type: Array, required: true },
    ownerOptions: { type: Array, default: () => [] },
    ownerLoading: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    error: { type: String, default: null },
    editing: { type: Object, default: null }
  },
  setup(props, { emit }) {
    const isEditing = computed(() => props.editing != null)
    const statusFieldKey = computed(() => (props.type === 'horse' ? 'horse_status_id' : 'rider_status_id'))

    // Zone is rider-only. On Add New it's resolved server-side by default (checkbox ticked); on
    // Backdate there's no checkbox — it's a plain manual field, pre-filled from the entry.
    const { zones: zoneOptions, loading: zoneLoading } = useZones()

    const form = reactive({
      boyStatus: null,
      status: null,
      effectiveDate: '',
      owner: null, // owner_id (primitive) — FSearchSelect works off ids, not option objects
      useAutoZone: true,
      zone: null
    })

    // Tracks the full {id, name} of whatever owner is currently chosen (start value, or
    // whatever the admin picks afterwards) — kept separate from the search-results page so a
    // fresh search (which replaces `ownerOptions` wholesale) never makes the current pick vanish
    // from the list and appear to "reset" the field.
    const selectedOwnerOption = ref(props.editing && props.editing.owner ? props.editing.owner : null)

    if (props.editing) {
      const statusField = props.type === 'horse' ? props.editing.horse_status : props.editing.rider_status
      form.boyStatus = props.statusOptions.find(o => o.id === props.editing.boy_status.id) || null
      form.status = props.statusOptions.find(o => o.id === statusField.id) || null
      form.effectiveDate = new Date(props.editing.effective_date).toISOString().slice(0, 10)
      if (props.type === 'horse' && props.editing.owner) {
        form.owner = props.editing.owner.id
      }
      if (props.type === 'rider' && props.editing.zone) {
        form.zone = { id: props.editing.zone.id, name: props.editing.zone.name }
      }
    }

    const ownerOptionsWithSelected = computed(() => {
      const current = selectedOwnerOption.value
      if (!current || props.ownerOptions.some(o => o.id === current.id)) return props.ownerOptions
      return [current, ...props.ownerOptions]
    })

    function onOwnerSelect(option) {
      selectedOwnerOption.value = option
    }

    // Only the pre-filled current owner can carry `deleted` (from the resource) — search
    // results from searchOwners() never include deleted members, so this is a no-op for them.
    function ownerLabel(option) {
      return option.deleted ? `${option.name} (deleted)` : option.name
    }

    function onOwnerSearch(query) {
      emit('owner-search', query)
    }

    // Owner is required on Backdate (existing entry) — Add New always auto-resolves it
    // server-side and never shows the field, so it's excluded from this check.
    const ownerRequired = computed(() => props.type === 'horse' && isEditing.value)

    // Zone is only required on Add New when the admin unticks auto-detect — Backdate's manual
    // field can be left blank (clears the zone) and Add New's auto path never shows the field.
    const zoneRequired = computed(() => props.type === 'rider' && !isEditing.value && !form.useAutoZone)

    // Client-side "required" checks below used to fail silently (Save just did nothing) — this
    // surfaces what's missing via the same alert box used for server errors.
    const validationError = ref(null)

    function handleSubmit() {
      validationError.value = null

      if (!form.boyStatus || !form.status || !form.effectiveDate) {
        validationError.value = 'Please fill in all required fields.'
        return
      }
      if (ownerRequired.value && !form.owner) {
        validationError.value = 'Owner is required.'
        return
      }
      if (zoneRequired.value && !form.zone) {
        validationError.value = 'Please select a zone, or tick "Automatically determine zone".'
        return
      }

      const payload = {
        boy_status_id: form.boyStatus.id,
        [statusFieldKey.value]: form.status.id,
        effective_date: form.effectiveDate
      }

      if (ownerRequired.value) {
        payload.owner_id = form.owner
      }

      if (props.type === 'rider') {
        if (isEditing.value) {
          // Plain manual field — always send whatever is picked, including a deliberate clear.
          payload.zone_id = form.zone ? form.zone.id : null
        } else {
          payload.use_auto_zone = form.useAutoZone
          if (!form.useAutoZone) {
            payload.zone_id = form.zone.id
          }
        }
      }

      emit('submit', payload)
    }

    return {
      form, isEditing, zoneOptions, zoneLoading, validationError, ownerOptionsWithSelected, onOwnerSelect, onOwnerSearch, ownerLabel, handleSubmit
    }
  }
})
</script>



// WEBPACK FOOTER //
// src/components/BoyStatus/BoyStatusFormModal.vue